import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Clock, FileText, ExternalLink, User } from "lucide-react";
import WikiSidebar from "./WikiSidebar";
import WikiHeader from "./WikiHeader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WikiContributions() {
  const { username } = useParams();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState("");
  const [contributions, setContributions] = useState([]);
  const [totalContributions, setTotalContributions] = useState(0);

  useEffect(() => {
    checkAuth();
  }, [username]);

  const checkAuth = async () => {
    const token = localStorage.getItem("wiki_token");
    if (!token) {
      navigate("/wiki/login");
      return;
    }

    try {
      const response = await axios.get(`${API}/wiki/verify?token=${token}`);
      if (response.data.valid) {
        setIsAuthenticated(true);
        setCurrentUser(response.data.username);
        fetchContributions(token);
      } else {
        navigate("/wiki/login");
      }
    } catch (error) {
      navigate("/wiki/login");
    }
  };

  const fetchContributions = async (token) => {
    try {
      const response = await axios.get(
        `${API}/wiki/contributions/${encodeURIComponent(username)}?token=${token}`
      );
      setContributions(response.data.contributions || []);
      setTotalContributions(response.data.total || 0);
    } catch (error) {
      toast.error("Failed to load contributions");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("wiki_token");
    navigate("/wiki/login");
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Unknown";
    try {
      const date = new Date(dateStr);
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#f6f6f6]">
        <WikiHeader username={currentUser} onLogout={handleLogout} />
        <div className="max-w-[1600px] mx-auto px-4 py-6">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map(i => (
                <div key={i} className="h-16 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f6f6]">
      <WikiHeader username={currentUser} onLogout={handleLogout} />
      
      {/* Page Title Bar */}
      <div className="bg-white border-b border-[#a2a9b1]">
        <div className="max-w-[1600px] mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <User className="w-6 h-6 text-[#54595d]" />
            <h1 className="text-2xl font-wiki text-[#202122]">
              User contributions
            </h1>
          </div>
          <p className="text-sm text-[#54595d] mt-1 ml-9">
            For <Link to={`/wiki/User:${encodeURIComponent(username)}`} className="text-[#3366cc] hover:underline">{username}</Link>
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1600px] mx-auto px-4 py-6">
        <div className="flex gap-6">
          <WikiSidebar username={username} showUserPage={true} />

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <div className="bg-white border border-[#a2a9b1] rounded">
              {/* Header */}
              <div className="px-4 py-3 border-b border-[#a2a9b1] bg-[#f8f9fa]">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-[#54595d]" />
                    <span className="font-medium text-[#202122]">Contribution History</span>
                  </div>
                  <span className="text-sm text-[#54595d]">
                    {totalContributions} contribution{totalContributions !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {/* Contributions List */}
              {contributions.length === 0 ? (
                <div className="px-4 py-8 text-center text-[#54595d]">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No contributions found for this user.</p>
                </div>
              ) : (
                <div className="divide-y divide-[#eaecf0]">
                  {contributions.map((contrib, index) => (
                    <div 
                      key={`${contrib.page_title}-${contrib.created_at}-${index}`}
                      className="px-4 py-3 hover:bg-[#f8f9fa] transition-colors"
                    >
                      <div className="flex items-start gap-4">
                        {/* Date */}
                        <div className="w-[180px] flex-shrink-0">
                          <span className="text-sm text-[#54595d]">
                            {formatDate(contrib.created_at)}
                          </span>
                        </div>

                        {/* Page Title */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/wiki/${encodeURIComponent(contrib.page_title)}`}
                              className="text-[#3366cc] hover:underline font-medium flex items-center gap-1"
                            >
                              {contrib.page_title}
                              <ExternalLink className="w-3 h-3" />
                            </Link>
                            {contrib.is_current && (
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                Current
                              </span>
                            )}
                          </div>
                          {contrib.content_preview && (
                            <p className="text-xs text-[#72777d] mt-1 truncate">
                              {contrib.content_preview}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex-shrink-0">
                          <Link
                            to={`/wiki/${encodeURIComponent(contrib.page_title)}?tab=history`}
                            className="text-xs text-[#3366cc] hover:underline"
                          >
                            View history
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
